import React, { useCallback, useState, useMemo, useRef, useEffect } from "react";
import { useStaticQuery, graphql } from "gatsby";
import slugify from "@sindresorhus/slugify";
import Slider from "react-slick";
import { useInView } from "react-intersection-observer";
import CardProdotto from "./card-prodotto";
import sortPosition from "../utility/sort-position";
import useProductPrices from "../hooks/use-product-prices";
import { productImpression } from "../utility/datalayer";

const settings = {
  dots: false,
  infinite: false,
  autoplay: false,
  arrows: true,
  swipe: true,
  variableWidth: true,
};

const GammaLine = ({ module, labelManagerLabels, config, origin, location, isFirst }) => {
  const [detail, setDetail] = useState(false);
  const toggleDetail = useCallback(() => setDetail((value) => !value), []);

  const sliderRef = useRef();
  const setSlider = useCallback((slider) => (sliderRef.current = slider), []);
  const [isMobile, setIsMobile] = useState(false);
  const updateMobile = useCallback(() => {
    if (sliderRef.current) {
      setIsMobile(sliderRef.current.state.breakpoint === 576);
    }
  }, []);

  const productList = useMemo(() => {
    const skus = module.products ? module.products.filter(Boolean) : [];
    return (
      module.product
        ?.filter((item) => !!item)
        ?.filter((product) => !skus.some((item) => item.key_prod === product.key_prod)) || []
    ).concat(skus);
  }, [module.product, module.products]);

  const productIds = useMemo(() => productList.map((product) => product.key_prod), [productList]);
  const priceInfo = useProductPrices(productIds, productList);

  const products = useMemo(() => {
    const list = productIds
      .map((pId) => productList.find((product) => product.key_prod === pId))
      .filter(Boolean);
    return priceInfo
      ? list.map((item) => {
          const itemData = priceInfo.get(item.key_prod);
          return {
            ...item,
            msrpPrice: itemData?.msrpPrice || null,
            sellingPrice: itemData?.sellingPrice || null,
            omnibusPrice: itemData?.omnibusPrice || null,
            shopLink: itemData?.shopLink || null,
            availableForSale: itemData?.availableForSale,
            special_from_date: itemData?.special_from_date,
            special_to_date: itemData?.special_to_date,
            contributoRaee: itemData?.contributo_raee ? itemData.contributo_raee : null,
          };
        })
      : list;
  }, [productList, productIds, priceInfo]);

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    // Call product impressions when prices are loaded AND the products are in view
    if (priceInfo && inView) {
      productImpression(products, origin);
    }
  }, [products, origin, priceInfo, inView]);

  const minischeda = useMemo(() => {
    return products.length ? products[0].pageLine.mainfieldsFilters.miniCard1 : [];
  }, [products]);
  const specs = useMemo(() => {
    return products.length
      ? products[0].attributes.filter((attr) => minischeda.includes(attr.id))
      : [];
  }, [products, minischeda]);

  const sliderSettings = useMemo(
    () => ({
      ...settings,
      slidesToShow: Math.min(4, products.length + 1),
      onInit: updateMobile,
      onReInit: updateMobile,
      responsive: [
        {
          breakpoint: 1200,
          settings: {
            slidesToShow: Math.min(3, products.length + 1),
          },
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: Math.min(2, products.length + 1),
          },
        },
        {
          breakpoint: 576,
          settings: {
            slidesToShow: 1,
            variableWidth: false,
            centerMode: true,
            centerPadding: "15px",
          },
        },
      ],
    }),
    [products.length, updateMobile]
  );

  const { liferayProductNotes } = useStaticQuery(
    graphql`
      {
        liferayProductNotes {
          ...ProductNotesFragment
        }
      }
    `
  );

  return (
    <>
      {module.anchorDesktop && (
        <span id={slugify(module.anchorDesktop)} className="tabs__scroll-anchor" />
      )}
      <section ref={ref} className="section gamma">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              {module.gammaTitle &&
                (isFirst ? (
                  <h1 className="section__title d-none d-sm-block">{module.gammaTitle}</h1>
                ) : (
                  <h2 className="section__title d-none d-sm-block">{module.gammaTitle}</h2>
                ))}
              {module.intro?.title &&
                (isFirst ? (
                  <h1 className="gamma__title d-sm-none mb-0 text-center">{module.intro.title}</h1>
                ) : (
                  <h2 className="gamma__title d-sm-none mb-0 text-center">{module.intro.title}</h2>
                ))}
              <div className="gamma__products d-sm-none text-center">
                {products.length}{" "}
                {products.length > 1 ? labelManagerLabels.products : labelManagerLabels.product}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12 px-0 px-md-3">
              <div className={`gamma__line${detail ? " open" : ""}`}>
                <Slider ref={setSlider} {...sliderSettings} className="slider-gamma mt-4 mt-sm-0">
                  {!isMobile && (
                    <div className="gamma__slide d-flex flex-column">
                      <div className="gamma__card gamma__card--first d-flex flex-column flex-fill">
                        <div className="p-4 flex-fill">
                          {module.intro?.title && (
                            <h3 className="gamma__title d-none d-sm-block">{module.intro.title}</h3>
                          )}
                          <div className="gamma__products mt-2">
                            {products.length}{" "}
                            {products.length > 1
                              ? labelManagerLabels.products
                              : labelManagerLabels.product}
                          </div>
                          {module.intro?.abstract && (
                            <div
                              className="gamme__subtitle mt-3"
                              dangerouslySetInnerHTML={{
                                __html: module.intro?.abstract,
                              }}
                            />
                          )}
                        </div>
                        <button className="gamma__btn gamma__btn--detail" onClick={toggleDetail}>
                          {detail
                            ? labelManagerLabels.closeDetails
                            : labelManagerLabels.openDetails}
                        </button>
                      </div>
                      <div className="gamma__specs-wrapper">
                        <div className="gamma__specs">
                          {specs.map((attr) => (
                            <div className="gamma__spec" key={attr.id}>
                              {attr.fieldName}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {products.map((item) => (
                    <div className="gamma__slide d-flex flex-column" key={item.key_prod}>
                      <CardProdotto
                        data={item}
                        className="prodotti__card--gamma"
                        details={minischeda}
                        location={location}
                        showDetails={detail}
                        labelManagerLabels={labelManagerLabels}
                        config={config}
                        origin={origin}
                        addCart={true}
                        productNote={liferayProductNotes}
                      />
                    </div>
                  ))}
                </Slider>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const SliderGamma = ({ data, labelManagerLabels, config, origin, location, isFirst }) => {
  const modules = useMemo(
    () =>
      data.moduleTitleFieldSet.length
        ? data.moduleTitleFieldSet
            .filter(
              (module) =>
                module.product?.some((item) => !!item) ||
                (module.products && module.products.some(Boolean))
            )
            .sort(sortPosition)
        : data.moduleTitleFieldSet,
    [data.moduleTitleFieldSet]
  );

  return (
    <>
      {modules.map((module, i) => (
        <GammaLine
          module={module}
          labelManagerLabels={labelManagerLabels}
          config={config}
          origin={origin}
          location={location}
          isFirst={isFirst}
          key={i}
        />
      ))}
    </>
  );
};

export default SliderGamma;
export const fragment = graphql`
  fragment CompareFragment on LiferayCompare {
    id
    liferayFields {
      siteId
      articleId
    }
    #productLine
    moduleTitleFieldSet {
      anchorDesktop
      anchorMobile
      position
      gammaTitle: title
      skusText
      products {
        ...BabelProdottoAttributesFragment
      }
      sku
      product {
        ...BabelProdottoAttributesFragment
      }
      intro {
        ... on LiferayIntroCompare {
          title
          abstract
        }
      }
    }
  }
`;
